<template>
  <div>
    <Loader v-if="loader" />

    <div class="page">
      <div class="content">
        <div class="dropdown-wrapper mb-2">
          <span class="label">SELEZIONA CLIENTE</span>
          <br />
          <Dropdown
            :options="clientOptions"
            :filter="true"
            filterBy="displayName,piva,codfis"
            optionKey="id"
            v-model="selectedClient"
            :optionLabel="formatClientOption"
            placeholder="CONDIZIONE CLIENTE"
            class="dropdown"
            @change="onClientSelected"
          />
          <div v-show="validationErrors.client && submitted" class="p-error">
            Campo richiesto.
          </div>
        </div>
        <div v-if="contractOptions.length" class="dropdown-wrapper mb-2 mt-3">
          <span class="label">SELEZIONA CONTRATTO</span>
          <br />
          <Dropdown
            :options="contractOptions"
            :filter="true"
            v-model="selectedContract"
            :optionLabel="formatContractOption"
            placeholder="CONTRATTO"
            class="dropdown"
          />
        </div>
        <div v-if="selectedClient" class="date-wrapper mt-3">
          <span class="label">Data della Voltura</span>
          <br />
          <input
            type="date"
            v-model="volturaDate"
            @change="updateDataInizio"
            class="date-input"
          />
          <div v-show="validationErrors.date && submitted" class="p-error">
            Campo richiesto.
          </div>
        </div>
        <button
          v-if="selectedClient && selectedContract && volturaDate"
          @click="onSendVoltura"
          class="export-button mt-3"
        >
          <i class="pi pi-check" style="margin-right: 8px"></i>
          Invia Voltura
        </button>
        <div v-if="successMessage" class="success-message mt-3">
          {{ successMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, getCurrentInstance, onMounted } from "vue";
import VolturaService from "@/services/ask-operation/voltura-service";
import Loader from "@/components/Loader.vue";
import Dropdown from "primevue/dropdown";

export default {
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  components: {
    Loader,
    Dropdown,
  },
  setup(props) {
    const page = { name: "DialogVoltura" };
    const loader = ref(false);
    const podId = ref(props.data?.id || null);
    const self = getCurrentInstance();
    self.$eventBus = self.appContext.config.globalProperties.$eventBus;
    self.$http = self.appContext.config.globalProperties.$http;
    const volturaService = new VolturaService(self.$http, self.$eventBus);

    const clientOptions = ref([]);
    const contractOptions = ref([]);
    const selectedClient = ref(null);
    const selectedContract = ref(null);
    const volturaDate = ref(null);
    const validationErrors = ref({});
    const submitted = ref(false);
    const successMessage = ref("");

    const formatContractOption = (contract) =>
      `${contract.codeContratto} - Data Inizio: ${contract.data_inizio}`;

    const formatClientOption = (client) => {
      const displayName = client.ragsoc || `${client.nome} ${client.cognome}`.trim();
      const piva = client.piva ? `P.IVA: ${client.piva}` : '';
      const codfis = client.codfis ? `C.F.: ${client.codfis}` : '';
      return `${displayName} ${piva} ${codfis}`.trim();
    };

    const updateDataInizio = () => {
      console.log("Data Inizio aggiornata:", volturaDate.value);
    };

    const onClientSelected = async () => {
      if (!selectedClient.value) return;

      loader.value = true;

      try {
        const response = await volturaService.sendClientData({
          id: selectedClient.value.id,
        });
        if (response.success) {
          contractOptions.value = response.data
            .sort((a, b) => new Date(b.data_inizio) - new Date(a.data_inizio))
            .map((contract) => ({
              code: contract.codeContratto,
              cod_old_cliente: contract.cod_cliente,
              cod_contratto: contract.id,
              data_inizio: contract.data_inizio,
              ...contract,
            }));
        }
      } catch (error) {
        console.error("Errore nel caricamento dei contratti:", error);
      } finally {
        loader.value = false;
      }
    };

    const validateForm = () => {
      validationErrors.value = {};
      if (!selectedClient.value) validationErrors.value.client = true;
      if (!volturaDate.value) validationErrors.value.date = true;
      return !Object.keys(validationErrors.value).length;
    };

    const onSendVoltura = async () => {
      if (!validateForm() || !podId.value) {
        console.error("Tutti i campi devono essere compilati.");
        validationErrors.value.client = !selectedClient.value;
        validationErrors.value.date = !volturaDate.value;
        return;
      }

      loader.value = true;

      try {
        const payload = {
          id: podId.value,
          cod_contratto: selectedContract.value?.cod_contratto,
          cod_cliente: selectedClient.value?.id,
          data_inizio: volturaDate.value,
          save: true,
        };

        const response = await volturaService.submitVolturaContratto(payload);
        console.log("Risposta dal backend:", response);

        selectedClient.value = null;
        selectedContract.value = null;
        volturaDate.value = null;
        contractOptions.value = [];
        successMessage.value = "La voltura è stata inserita a sistema!";
      } catch (error) {
        console.error("Errore nella richiesta:", error);
        successMessage.value =
          "Errore durante l'operazione. Si prega di riprovare.";
      } finally {
        loader.value = false;
      }
    };

    const fetchData = async () => {
      try {
        const clients = await volturaService.getClients();
        clientOptions.value = clients.items.map((client) => ({
          ...client,
          id: client.id,
          displayName: client.ragsoc || `${client.nome} ${client.cognome}`.trim(),
          piva: client.piva || '',
          codfis: client.codfis || '',
        }));
      } catch (error) {
        console.error("Errore nel caricamento dei clienti:", error);
      }
    };

    onMounted(() => {
      fetchData();
    });

    return {
      page,
      podId,
      loader,
      clientOptions,
      contractOptions,
      selectedClient,
      selectedContract,
      volturaDate,
      validationErrors,
      submitted,
      successMessage,
      updateDataInizio,
      validateForm,
      onClientSelected,
      formatContractOption,
      formatClientOption,
      onSendVoltura,
    };
  },
};
</script>



<style scoped>
.page {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 20px;
}

.content {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
}

.dropdown-wrapper {
  text-align: center;
  margin-bottom: 10px;
}

.dropdown-wrapper span.label {
  display: inline-block;
  color: #000;
  text-transform: uppercase;
  font-size: 12px;
  margin-bottom: 8px;
}

.dropdown {
  width: 260px;
  padding: 8px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 5px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.date-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.date-input {
  width: 260px;
  padding: 8px;
  font-size: 14px;
  margin-top: 10px;
  text-align: center;
  border: 1px solid #ccc;
  border-radius: 5px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.export-button {
  background-color: #4a90e2;
  color: white;
  border: none;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  cursor: pointer;
}

.export-button:hover {
  background-color: #357ab8;
}

.success-message {
  color: green;
  font-size: 14px;
  font-weight: bold;
  text-align: center;
}

</style>